(function () {
  if (customElements.get('bst-contact-form')) return;

  class BstContactForm extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      if (!this.form) return;

      this.errorHeading =
        this.dataset.errorHeading || 'Please adjust the following:';
      this.clientErrorBanner = this.querySelector('[data-bst-client-errors]');

      this.onSubmit = this.onSubmit.bind(this);
      this.onFieldChange = this.onFieldChange.bind(this);

      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('input', this.onFieldChange);
      this.form.addEventListener('change', this.onFieldChange);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this.onSubmit);
      this.form.removeEventListener('input', this.onFieldChange);
      this.form.removeEventListener('change', this.onFieldChange);
    }

    onSubmit(event) {
      if (!this.validate()) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    onFieldChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!this.form.contains(target)) return;

      if (target.type === 'radio') {
        const group = target.closest('[data-bst-validate-group]');
        if (group) this.clearGroupError(group);
        return;
      }

      if (target.matches('input, select, textarea')) {
        this.clearControlError(target);
      }
    }

    validate() {
      this.clearAllErrors();
      const errors = [];

      this.form.querySelectorAll('[data-bst-validate-group]').forEach((group) => {
        if (group.getAttribute('aria-required') !== 'true') return;

        const radio = group.querySelector('input[type="radio"]');
        if (!radio) return;

        const checked = this.form.querySelector(
          `input[type="radio"][name="${CSS.escape(radio.name)}"]:checked`
        );

        if (!checked) {
          const label = this.getValidateLabel(group, 'This field');
          const message = `Please select ${label.toLowerCase()}.`;
          this.showGroupError(group, message);
          errors.push({ element: group, message, label });
        }
      });

      this.form.querySelectorAll('input, select, textarea').forEach((control) => {
        if (control.type === 'radio' || control.type === 'hidden') return;
        if (!control.required) return;
        if (control.disabled) return;

        const label = this.getControlLabel(control);
        let message = null;

        if (control.type === 'checkbox') {
          if (!control.checked) {
            message = `Please confirm ${label.toLowerCase()}.`;
          }
        } else if (control.tagName === 'SELECT') {
          if (!control.value) {
            message = `Please select ${label.toLowerCase()}.`;
          }
        } else if (!control.value.trim()) {
          message = `Please enter ${label.toLowerCase()}.`;
        } else if (control.type === 'email' && !control.checkValidity()) {
          message = 'Please enter a valid email address.';
        }

        if (message) {
          this.showControlError(control, message);
          errors.push({ element: control, message, label });
        }
      });

      if (errors.length) {
        this.showErrorBanner(errors);
        const firstTarget = errors[0].element;
        firstTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });

        if (firstTarget.matches('input, select, textarea')) {
          firstTarget.focus({ preventScroll: true });
        } else {
          const focusable = firstTarget.querySelector('input, select, textarea');
          focusable?.focus({ preventScroll: true });
        }

        return false;
      }

      this.hideErrorBanner();
      return true;
    }

    getValidateLabel(node, fallback) {
      const label = node.dataset.bstValidateLabel;
      return label && label.trim() ? label.trim() : fallback;
    }

    getControlLabel(control) {
      const wrapper = control.closest('[data-bst-validate-label]');
      if (wrapper) return this.getValidateLabel(wrapper, 'this field');

      const labelEl = control.id
        ? this.form.querySelector(`label[for="${CSS.escape(control.id)}"]`)
        : null;

      if (labelEl) {
        return labelEl.textContent.replace(/\*/g, '').trim() || 'this field';
      }

      return control.name || 'this field';
    }

    showControlError(control, message) {
      control.setAttribute('aria-invalid', 'true');

      const errorId = control.id
        ? `${control.id}-client-error`
        : `client-error-${Math.random().toString(36).slice(2, 9)}`;

      const container =
        control.type === 'checkbox'
          ? control.closest('.bst-contact-form__privacy') || control.parentElement
          : control.closest('.bst-contact-form__field') || control.parentElement;

      let errorEl = container.querySelector(`[data-bst-client-error][data-bst-for="${errorId}"]`);

      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'bst-contact-form__field-error';
        errorEl.setAttribute('role', 'alert');
        errorEl.dataset.bstClientError = 'true';
        errorEl.dataset.bstFor = errorId;
        errorEl.id = errorId;

        if (control.type === 'checkbox') {
          container.appendChild(errorEl);
        } else {
          control.insertAdjacentElement('afterend', errorEl);
        }
      }

      errorEl.textContent = message;
      control.setAttribute('aria-describedby', errorId);
    }

    showGroupError(group, message) {
      group.setAttribute('aria-invalid', 'true');

      const errorId = `${group.getAttribute('aria-labelledby') || 'group'}-client-error`;
      let errorEl = group.nextElementSibling;

      if (
        !errorEl ||
        !errorEl.matches('[data-bst-client-error]') ||
        errorEl.dataset.bstGroupFor !== group.id
      ) {
        errorEl = document.createElement('p');
        errorEl.className = 'bst-contact-form__field-error';
        errorEl.setAttribute('role', 'alert');
        errorEl.dataset.bstClientError = 'true';
        errorEl.dataset.bstGroupFor = group.id || '';
        group.insertAdjacentElement('afterend', errorEl);
      }

      errorEl.id = errorId;
      errorEl.textContent = message;
      group.setAttribute('aria-describedby', errorId);
    }

    showErrorBanner(errors) {
      if (!this.clientErrorBanner) return;

      this.clientErrorBanner.hidden = false;
      this.clientErrorBanner.innerHTML = '';

      const heading = document.createElement('h2');
      heading.textContent = this.errorHeading;
      this.clientErrorBanner.appendChild(heading);

      const list = document.createElement('ul');
      list.className = 'bst-contact-form__error-list';

      errors.forEach(({ element, message, label }) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${element.id || ''}`;
        link.textContent = message;

        link.addEventListener('click', (event) => {
          event.preventDefault();
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const focusTarget = element.matches('input, select, textarea')
            ? element
            : element.querySelector('input, select, textarea');
          focusTarget?.focus({ preventScroll: true });
        });

        item.appendChild(link);
        list.appendChild(item);
      });

      this.clientErrorBanner.appendChild(list);
      this.clientErrorBanner.focus({ preventScroll: true });
    }

    hideErrorBanner() {
      if (!this.clientErrorBanner) return;
      this.clientErrorBanner.hidden = true;
      this.clientErrorBanner.innerHTML = '';
    }

    clearControlError(control) {
      control.removeAttribute('aria-invalid');
      control.removeAttribute('aria-describedby');

      const container =
        control.type === 'checkbox'
          ? control.closest('.bst-contact-form__privacy') || control.parentElement
          : control.closest('.bst-contact-form__field') || control.parentElement;

      container
        ?.querySelectorAll('[data-bst-client-error]')
        .forEach((errorEl) => errorEl.remove());
    }

    clearGroupError(group) {
      group.removeAttribute('aria-invalid');
      group.removeAttribute('aria-describedby');

      const errorEl = group.nextElementSibling;
      if (errorEl?.matches('[data-bst-client-error]')) {
        errorEl.remove();
      }
    }

    clearAllErrors() {
      this.form
        .querySelectorAll('[aria-invalid="true"]')
        .forEach((el) => el.removeAttribute('aria-invalid'));

      this.form.querySelectorAll('[data-bst-client-error]').forEach((el) => el.remove());
      this.form
        .querySelectorAll('[aria-describedby]')
        .forEach((el) => {
          if (el.getAttribute('aria-describedby')?.includes('client-error')) {
            el.removeAttribute('aria-describedby');
          }
        });

      this.hideErrorBanner();
    }
  }

  customElements.define('bst-contact-form', BstContactForm);
})();
